import { RefObject, useEffect, useRef, useState } from "react";
import { SubMenuKey, useSubMenuContext } from "./sub-menu-context";

const useOnMenuViewStateChange = (
  menuRef: RefObject<HTMLDivElement>,
  onViewStateChange: (isMenuExpanded: boolean) => void
) => {
  const SIZE_OF_EXPANDED_MENU = 192;
  const resizeObserverRef = useRef<ResizeObserver>();
  const prevMenuExpandedRef = useRef<boolean | undefined>(undefined);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const onResize = () => {
    if (menuRef.current) {
      const { clientWidth } = menuRef.current;
      const isMenuExpandedCalculated = clientWidth > SIZE_OF_EXPANDED_MENU / 2;
      if (prevMenuExpandedRef.current === isMenuExpandedCalculated) {
        return;
      }
      prevMenuExpandedRef.current = isMenuExpandedCalculated;
      onViewStateChange(isMenuExpandedCalculated);
      setIsMenuExpanded(isMenuExpandedCalculated);
    }
  };
  useEffect(() => {
    const menuContainer = menuRef.current;
    if (menuContainer && ResizeObserver && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(onResize);
      resizeObserverRef.current.observe(menuContainer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuRef.current]);
  return { isMenuExpanded };
};

export const useCollapseSubMenu = (subMenuKey: SubMenuKey) => {
  const { openedSubMenu, toggleSubMenu, setOpenedSubMenu } =
    useSubMenuContext();
  const subMenuOpened = openedSubMenu === subMenuKey;
  const menuRef = useRef<HTMLDivElement>(null);

  const { isMenuExpanded } = useOnMenuViewStateChange(
    menuRef,
    (isMenuExpanded) => {
      if (!menuRef.current) {
        return;
      }
      const selectedItem = menuRef.current.querySelector("a.active");

      if (isMenuExpanded && selectedItem) {
        const scrollToMenuItem = () => {
          scrollToElement(
            menuRef.current?.closest("#grouped-menu"),
            selectedItem
          );
        };

        setOpenedSubMenu(subMenuKey);

        scrollToMenuItem();
      } else if (!isMenuExpanded && !selectedItem) {
        setOpenedSubMenu(null);
      }
    }
  );

  const handleToggle = () => {
    toggleSubMenu(subMenuKey);
  };

  const handleOpen = () => {
    setOpenedSubMenu(subMenuKey);
  };

  return {
    menuRef,
    subMenuOpened,
    toggleSubMenu: handleToggle,
    openSubMenu: handleOpen,
    isMenuExpanded,
  };
};

function scrollToElement(
  container: Element | null | undefined,
  element: Element,
  offset = 0
) {
  if (!container) {
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (
    elementRect.top > containerRect.top &&
    elementRect.bottom < containerRect.bottom
  ) {
    return;
  }
  const top =
    elementRect.top - containerRect.top + container.scrollTop - offset;

  container.scrollTo({ top });
}
